import { useState } from 'react';
import type { FormEvent } from 'react';
import type { RecipeImportPrefill } from '@mlc/shared';
import { ApiError, importFromUrl } from './api';
import { Banner, Button, Card, fieldClasses } from './ui';

export interface ImportUrlFormProps {
  onImported: (prefill: RecipeImportPrefill) => void;
  onCancel: () => void;
  /** Bascule vers le collage manuel de texte (US-03), proposé explicitement quand l'import échoue faute de balisage et de clé IA. */
  onOfferPaste: () => void;
}

/** Code renvoyé par le serveur quand la page n'a pas de balisage structuré et qu'aucune clé LLM n'est configurée (§4.4 étape 3, AC : « propose le collage manuel, avec un message explicite »). */
const NO_MARKUP_NO_KEY_CODE = 'import_markup_absent_no_llm';

// Style de champ partagé (B1, refonte UX/UI passe 3) : voir ui/Field.tsx.
const inputClass = fieldClasses('default');

/**
 * Import depuis une URL (US-02) : un des points d'entrée de l'écran
 * « Ajout / édition » (§6), avant `RecipeForm`. Le résultat n'est jamais
 * enregistré directement — il pré-remplit `RecipeForm`, qui reste l'unique
 * écran de validation (§4.4 étape 4).
 */
export function ImportUrlForm({ onImported, onCancel, onOfferPaste }: ImportUrlFormProps): React.JSX.Element {
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerPaste, setOfferPaste] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = url.trim();
    if (trimmed.length === 0 || submitting) return;

    setSubmitting(true);
    setError(null);
    setOfferPaste(false);
    importFromUrl({ url: trimmed })
      .then(onImported)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Erreur inconnue.');
        setOfferPaste(err instanceof ApiError && err.code === NO_MARKUP_NO_KEY_CODE);
      })
      .finally(() => { setSubmitting(false); });
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Importer depuis une URL" className="flex flex-col gap-4">
      {error !== null && (
        <Banner tone="danger" as="div" className="flex flex-col gap-2">
          <p>{error}</p>
          {offerPaste && (
            <p>
              <button type="button" onClick={onOfferPaste} className="underline">
                Coller le texte de la recette à la place
              </button>
            </p>
          )}
        </Banner>
      )}

      {/* Mise en contexte (audit UX/UI passe 2, I7 : « pas d'illustration du
          principe, pas de mise en contexte » — traitement jusqu'ici réduit à
          un champ et deux boutons). */}
      <Card surface className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="mt-0.5 size-6 shrink-0 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 15l6-6M10.5 6.5l1-1a4 4 0 0 1 5.66 5.66l-1.5 1.5M13.5 17.5l-1 1a4 4 0 0 1-5.66-5.66l1.5-1.5" />
          </svg>
          <div className="flex flex-col gap-1">
            <h2 className="text-heading-2">Importer depuis une URL</h2>
            <p className="text-sm text-foreground-secondary">
              Collez le lien d’une page recette : titre, ingrédients et étapes sont préremplis automatiquement — vous
              validez tout avant l’enregistrement.
            </p>
          </div>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          Lien de la recette
          <input
            className={inputClass}
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => { setUrl(e.target.value); }}
            placeholder="https://…"
            required
            aria-required="true"
          />
        </label>
      </Card>

      <div className="flex gap-3">
        {/* §4.4 : « en moins de 30 secondes ou échoue avec un message
            actionnable » — l'attente peut être notable, et c'est `aria-busy`
            qui la dit : §4 interdit au libellé de changer pendant l'action. */}
        <Button type="submit" variant="primary" disabled={url.trim().length === 0 || submitting} aria-busy={submitting}>
          Importer
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
